import { useEffect, useRef } from 'react';
import getMobilePlatform from '../utils/getMobilePlatform';
import { PLATFORM } from '../constants';

interface BodyPosition {
  position: string;
  top: string;
  left: string;
  width: string;
  height: string;
}

export default function usePreventBodyScroll(isOpen: boolean) {
  const bodyPosition = useRef<BodyPosition | null>(null);
  const isIos = getMobilePlatform() === PLATFORM.IOS;

  useEffect(() => {
    function setBodyPositionFixed() {
      if (bodyPosition.current) {
        return;
      }

      const { position, width, height } = document.body.style;
      const { scrollY, scrollX } = window;

      const newPrevBodyPosition: BodyPosition = {
        position,
        top: String(scrollY),
        left: String(scrollX),
        width,
        height,
      };

      document.body.style.setProperty('position', 'fixed');
      document.body.style.setProperty('top', `${-scrollY}px`);
      document.body.style.setProperty('left', `${-scrollX}px`);
      document.body.style.setProperty('width', '100%');
      document.body.style.setProperty('height', '100%');
      document.body.style.setProperty('overflow', 'hidden');

      bodyPosition.current = { ...newPrevBodyPosition };
    }

    function restoreBodyPosition() {
      if (
        typeof window === 'undefined' ||
        typeof document === 'undefined' ||
        !bodyPosition.current
      ) {
        return;
      }

      const { position, top, left, width, height } = bodyPosition.current;

      document.body.style.setProperty('position', position);
      document.body.style.setProperty('top', '');
      document.body.style.setProperty('left', '');
      document.body.style.setProperty('width', width);
      document.body.style.setProperty('height', height);
      document.body.style.setProperty('overflow', '');

      window.scrollTo(parseInt(left, 10), parseInt(top, 10));

      bodyPosition.current = null;
    }

    function setBodyOverflowHidden() {
      document.body.style.setProperty('overflow', 'hidden');

      bodyPosition.current = null;
    }

    function restoreBodyOverflow() {
      document.body.style.setProperty('overflow', '');

      bodyPosition.current = null;
    }

    const turnOnBodyScrollLock = isIos ? setBodyPositionFixed : setBodyOverflowHidden;
    const turnOffBodyScrollLock = isIos ? restoreBodyPosition : restoreBodyOverflow;

    if (isOpen) {
      turnOnBodyScrollLock();
    } else {
      turnOffBodyScrollLock();
    }

    return () => {
      turnOffBodyScrollLock();
    };
  }, [isOpen]);
}
